import {
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useState,
  type CSSProperties,
} from "react";
import clsx from "clsx";
import { createPortal } from "react-dom";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import type { POI, Tag } from "@poi-planner/shared";
import { useInstanceSettingsStore } from "../store/instanceSettingsStore";
import { useMapViewPrefsStore } from "../store/mapViewPrefsStore";
import { useUIStore } from "../store/uiStore";
import type { MapStyleName } from "../store/mapViewPrefsStore";
import type { MapBounds } from "../hooks/usePoiFilters";
import {
  DEFAULT_PHOSPHOR_ICON,
  getCustomIconDataUrl,
  parseTagIcon,
} from "../utils/tagIcons";
import {
  mapViewportFitSignature,
  resolveMapViewportIntent,
} from "../utils/mapViewport";
import { getMapColors, resolveCssColor } from "../theme/colors";
import { Button } from "./Button";
import { MapLayerControl } from "./MapLayerControl";
import { BasemapPlacePopup } from "./BasemapPlacePopup";
import {
  basemapPlaceFromFeature,
  type BasemapPlace,
} from "../utils/basemapPlaces";
import { basemapPlaceMapsUrl } from "../utils/poiMaps";

export interface RouteLine {
  /** Ordered coordinates for the route [lng, lat][] */
  coordinates: [number, number][];
  /** POI IDs in order, for click handling */
  poiIds: string[];
  date?: string;
}

interface MapViewProps {
  pois: POI[];
  tags?: Tag[];
  selectedPoiId: string | null;
  /** Increment to force fly-to even if selection hasn't changed */
  flyToTrigger?: number;
  onSelectPoi: (id: string | null) => void;
  /** Called when map viewport changes (debounced) */
  onBoundsChange?: (bounds: MapBounds) => void;
  /** Optional route line connecting POIs in order (for day focus view) */
  routeLine?: RouteLine;
  /** Separate route segments; segments are never connected across dates. */
  routeLines?: RouteLine[];
  /** Optional POI order mapping to render route badges on POI markers */
  routeOrderByPoiId?: Map<string, number>;
  /** Optional POIs to fit into view when the parent changes context */
  fitToPois?: POI[];
  fitToPoisKey?: string | number;
  /**
   * Screen space covered by overlays (a floating toolbar above, a results
   * card below). The camera centres within what is left, and the map's own
   * controls move clear of both.
   */
  viewportInsets?: { top: number; bottom: number };
  /** poiId -> number shown on its pin, matching a numbered results list. */
  resultNumberByPoiId?: Map<string, number>;
}

const SOURCE_ID = "pois";
const CLUSTER_LAYER = "clusters";
const CLUSTER_COUNT_LAYER = "cluster-count";
const POI_SYMBOL_LAYER = "unclustered-poi-icons";
const ROUTE_POI_SOURCE_ID = "route-pois";
const ROUTE_POI_SYMBOL_LAYER = "route-pois-icons";
const ROUTE_POI_ORDER_LAYER = "route-pois-order";
const POI_RESULT_NUMBER_LAYER = "unclustered-poi-result-numbers";
const ROUTE_POI_RESULT_NUMBER_LAYER = "route-pois-result-numbers";
const ROUTE_SOURCE_ID = "route-line";
const ROUTE_LAYER_ID = "route-line-layer";

const PHOSPHOR_DUOTONE_BASE_URL =
  "https://unpkg.com/@phosphor-icons/core@2.1.1/assets/duotone";
const PHOSPHOR_RUNTIME_ICON_SIZE = 24;
const PHOSPHOR_RUNTIME_ICON_PIXEL_RATIO = 2;
const MARKER_NORMAL_SIZE = 40;
const MARKER_SELECTED_SIZE = 52;
const MARKER_NORMAL_RADIUS = 17;
const MARKER_SELECTED_RADIUS = 22;
const MARKER_NORMAL_STROKE_WIDTH = 2.7;
const MARKER_SELECTED_STROKE_WIDTH = 3.9;
const MARKER_NORMAL_GLYPH_SIZE = 24;
const MARKER_SELECTED_GLYPH_SIZE = 28;

const CLUSTER_POPUP_MAX_NAMES = 8;

const MAPBOX_STYLE_STANDARD = "mapbox://styles/mapbox/standard";
const MAPBOX_STYLE_STANDARD_SATELLITE =
  "mapbox://styles/mapbox/standard-satellite";

function mapStyleToUrl(style: MapStyleName): string {
  return style === "standard-satellite"
    ? MAPBOX_STYLE_STANDARD_SATELLITE
    : MAPBOX_STYLE_STANDARD;
}

// Interactive featureset published by the Mapbox Standard styles for their own
// tile labels. Standard is wrapped as an implicit "basemap" import by GL JS,
// which is also why config properties are set against that import id.
const BASEMAP_IMPORT_ID = "basemap";
const BASEMAP_POI_FEATURESET_ID = "poi";
const BASEMAP_POI_CLICK_INTERACTION = "basemap-poi-click";
const BASEMAP_POI_ENTER_INTERACTION = "basemap-poi-mouseenter";
const BASEMAP_POI_LEAVE_INTERACTION = "basemap-poi-mouseleave";

// Layers owning app markers; a click that lands on one of these must not also
// open a basemap point-of-interest popup underneath it.
const APP_INTERACTIVE_LAYERS = [
  CLUSTER_LAYER,
  POI_SYMBOL_LAYER,
  ROUTE_POI_SYMBOL_LAYER,
  ROUTE_POI_ORDER_LAYER,
  POI_RESULT_NUMBER_LAYER,
  ROUTE_POI_RESULT_NUMBER_LAYER,
] as const;

const TERRAIN_SOURCE_ID = "mapbox-dem";
const TERRAIN_EXAGGERATION = 1.5;

function resultNumberProperty(
  numbers: Map<string, number> | undefined,
  poiId: string,
): { resultNumber?: number } {
  const resultNumber = numbers?.get(poiId);
  return resultNumber === undefined ? {} : { resultNumber };
}

export function MapView({
  pois,
  tags = [],
  selectedPoiId,
  flyToTrigger,
  onSelectPoi,
  onBoundsChange,
  routeLine,
  routeLines,
  routeOrderByPoiId,
  fitToPois,
  fitToPoisKey,
  viewportInsets,
  resultNumberByPoiId,
}: MapViewProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  const boundsTimeoutRef = useRef<number | null>(null);
  const hasInitialFitRef = useRef(false);
  const lastFlyToPoiRef = useRef<string | null>(null);
  const lastFlyToTriggerRef = useRef<number | undefined>(undefined);
  const lastFitToPoisKeyRef = useRef<string | number | undefined>(undefined);
  const lastFitToPoisSignatureRef = useRef<string | null>(null);
  const lastMapReadyVersionRef = useRef(0);
  const mapLoadedRef = useRef(false);
  const resizeFrameRef = useRef<number | null>(null);
  const lastPoiClickTimestampRef = useRef<number>(0);
  const basemapPopupRef = useRef<mapboxgl.Popup | null>(null);
  const basemapPlaceClickTimestampRef = useRef<number | null>(null);
  const lastHoveredClusterIdRef = useRef<number | null>(null);
  const clusterHoverRequestIdRef = useRef(0);
  const customImageDataRef = useRef<Map<string, string>>(new Map());
  const markerImageSignatureRef = useRef<Map<string, string>>(new Map());
  const appliedMapStyleRef = useRef<MapStyleName | null>(null);
  const [mapReadyVersion, setMapReadyVersion] = useState(0);
  // Basemap (Mapbox tile) point of interest currently shown in its own popup.
  const [basemapPlace, setBasemapPlace] = useState<BasemapPlace | null>(null);
  // Host node for the basemap popup's React content; owned by this component
  // and handed to Mapbox as the popup's DOM content.
  const [basemapPopupNode] = useState(() =>
    typeof document === "undefined" ? null : document.createElement("div"),
  );
  // When locked, scroll/pinch/double-click/box gestures can't change the
  // current zoom level; the zoom buttons still work as a deliberate override.
  const [zoomLocked, setZoomLocked] = useState(false);
  // Host node for the zoom-lock button, added as its own control group
  // directly below the Navigation control's zoom buttons.
  const [zoomLockControlNode] = useState(() =>
    typeof document === "undefined" ? null : document.createElement("div"),
  );
  // Bumped whenever sources/layers/images/config are (re)registered, i.e. on
  // the initial "load" and on every subsequent "style.load" after a style
  // switch. Kept separate from mapReadyVersion so a style switch doesn't
  // also re-trigger the viewport fly/fit intent (Mapbox preserves the
  // camera across setStyle; only layer-dependent effects need to rerun).
  const [layersReadyVersion, setLayersReadyVersion] = useState(0);

  // Refs to avoid stale closures in map event handlers
  const selectedPoiIdRef = useRef(selectedPoiId);
  const onSelectPoiRef = useRef(onSelectPoi);
  const poiMapRef = useRef<Map<string, POI & { lat: number; lng: number }>>(
    new Map(),
  );
  const fitToPoisRef = useRef<POI[] | undefined>(fitToPois);
  const zoomLockedRef = useRef(zoomLocked);
  const getEffectivePoiIconUrlRef = useRef<(poi: POI) => string>(() => "");
  const getTagNamesRef = useRef<(poi: POI) => string[]>(() => []);
  const { settings } = useInstanceSettingsStore();
  const mapColors = useMemo(() => getMapColors(), []);

  // Keep refs in sync
  selectedPoiIdRef.current = selectedPoiId;
  onSelectPoiRef.current = onSelectPoi;
  fitToPoisRef.current = fitToPois;
  zoomLockedRef.current = zoomLocked;

  const mapboxApiKey = settings?.mapboxApiKey;
  const fitToPoisSignature = useMemo(
    () => mapViewportFitSignature(fitToPois),
    [fitToPois],
  );

  const mapStyle = useMapViewPrefsStore((s) => s.mapStyle);
  const setMapStyle = useMapViewPrefsStore((s) => s.setMapStyle);
  const showTransitLabels = useMapViewPrefsStore((s) => s.showTransitLabels);
  const setShowTransitLabels = useMapViewPrefsStore(
    (s) => s.setShowTransitLabels,
  );
  const showPointOfInterestLabels = useMapViewPrefsStore(
    (s) => s.showPointOfInterestLabels,
  );
  const setShowPointOfInterestLabels = useMapViewPrefsStore(
    (s) => s.setShowPointOfInterestLabels,
  );
  const showPlaceLabels = useMapViewPrefsStore((s) => s.showPlaceLabels);
  const setShowPlaceLabels = useMapViewPrefsStore((s) => s.setShowPlaceLabels);
  const showRoadLabels = useMapViewPrefsStore((s) => s.showRoadLabels);
  const setShowRoadLabels = useMapViewPrefsStore((s) => s.setShowRoadLabels);
  const show3dObjects = useMapViewPrefsStore((s) => s.show3dObjects);
  const setShow3dObjects = useMapViewPrefsStore((s) => s.setShow3dObjects);
  const lightPreset = useMapViewPrefsStore((s) => s.lightPreset);
  const setLightPreset = useMapViewPrefsStore((s) => s.setLightPreset);
  const terrainEnabled = useMapViewPrefsStore((s) => s.terrainEnabled);
  const setTerrainEnabled = useMapViewPrefsStore((s) => s.setTerrainEnabled);
  const [styleSwitching, setStyleSwitching] = useState(false);

  // Build a map of tag id -> tag for quick lookup
  const tagIdMap = useMemo(() => {
    const map = new Map<string, Tag>();
    for (const tag of tags) {
      map.set(tag.id, tag);
    }
    return map;
  }, [tags]);

  // Get the effective tag for a POI
  const getEffectiveTag = useCallback(
    (poi: POI): Tag | null => {
      if (poi.primaryTagId) {
        const tag = tagIdMap.get(poi.primaryTagId);
        if (tag) return tag;
      }
      if (poi.tags?.length > 0) {
        const tag = tagIdMap.get(poi.tags[0]);
        if (tag) return tag;
      }
      return null;
    },
    [tagIdMap],
  );

  const getTagNames = useCallback(
    (poi: POI) =>
      poi.tags
        .map((tagId) => tagIdMap.get(tagId)?.name)
        .filter((name): name is string => Boolean(name)),
    [tagIdMap],
  );
  getTagNamesRef.current = getTagNames;

  // Get the effective tag color for a POI
  const getEffectiveTagColor = useCallback(
    (poi: POI): string => {
      const tag = getEffectiveTag(poi);
      return tag?.color ? resolveCssColor(tag.color) : mapColors.defaultMarker;
    },
    [getEffectiveTag, mapColors.defaultMarker],
  );

  const getEffectiveMarkerImage = useCallback(
    (poi: POI): string => {
      const tag = getEffectiveTag(poi);
      const customDataUrl = getCustomIconDataUrl(tag?.icon);
      if (customDataUrl && tag) {
        return `custom-tag-${tag.id}`;
      }

      const parsed = parseTagIcon(tag?.icon);
      const iconName =
        parsed.kind === "phosphor" ? parsed.name : DEFAULT_PHOSPHOR_ICON;
      const selected = poi.id === selectedPoiId;
      return getMarkerImageId(
        iconName,
        selected ? mapColors.selectedFill : getEffectiveTagColor(poi),
        selected ? mapColors.selectedStroke : mapColors.iconFill,
        selected,
      );
    },
    [
      getEffectiveTag,
      getEffectiveTagColor,
      mapColors.iconFill,
      mapColors.selectedFill,
      mapColors.selectedStroke,
      selectedPoiId,
    ],
  );

  const hasCustomIcon = useCallback(
    (poi: POI): boolean => {
      const tag = getEffectiveTag(poi);
      if (!tag) return false;
      return !!getCustomIconDataUrl(tag.icon);
    },
    [getEffectiveTag],
  );

  // HTML-usable icon source (data URL or remote SVG) for a POI's effective
  // tag, for use in raw-HTML contexts like the cluster hover popup where the
  // Mapbox runtime image IDs used by getEffectiveMarkerImage aren't renderable.
  const getEffectivePoiIconUrl = useCallback(
    (poi: POI): string => {
      const tag = getEffectiveTag(poi);
      const parsed = parseTagIcon(tag?.icon);
      return parsed.kind === "custom"
        ? parsed.dataUrl
        : getPhosphorIconUrl(parsed.name);
    },
    [getEffectiveTag],
  );
  getEffectivePoiIconUrlRef.current = getEffectivePoiIconUrl;

  // Filter POIs that have valid coordinates
  const poisWithCoords = useMemo(
    () =>
      pois.filter(
        (poi): poi is POI & { lat: number; lng: number } =>
          poi.lat != null &&
          poi.lng != null &&
          !isNaN(poi.lat) &&
          !isNaN(poi.lng),
      ),
    [pois],
  );

  const effectiveRouteLines = useMemo(
    () => routeLines ?? (routeLine ? [routeLine] : []),
    [routeLine, routeLines],
  );

  const routePoiEntries = useMemo(() => {
    const entries: { poiId: string; coord: [number, number] }[] = [];
    const seen = new Set<string>();
    for (const line of effectiveRouteLines) {
      for (let i = 0; i < line.poiIds.length; i += 1) {
        const poiId = line.poiIds[i];
        const coord = line.coordinates[i];
        if (poiId && coord && !seen.has(poiId)) {
          seen.add(poiId);
          entries.push({ poiId, coord });
        }
      }
    }
    return entries;
  }, [effectiveRouteLines]);

  const routePoiIdSet = useMemo(
    () => new Set(routePoiEntries.map((entry) => entry.poiId)),
    [routePoiEntries],
  );

  const customTagIcons = useMemo(() => {
    const icons: Array<{ imageId: string; dataUrl: string }> = [];
    for (const tag of tags) {
      const dataUrl = getCustomIconDataUrl(tag.icon);
      if (!dataUrl) continue;
      icons.push({ imageId: `custom-tag-${tag.id}`, dataUrl });
    }
    return icons;
  }, [tags]);

  const standardMarkerImages = useMemo(() => {
    const images = new Map<
      string,
      {
        imageId: string;
        url: string;
        backgroundColor: string;
        borderColor: string;
        selected: boolean;
      }
    >();

    for (const poi of poisWithCoords) {
      const tag = getEffectiveTag(poi);
      const parsed = parseTagIcon(tag?.icon);
      if (parsed.kind !== "phosphor") continue;

      const selected = poi.id === selectedPoiId;
      const backgroundColor = selected
        ? mapColors.selectedFill
        : getEffectiveTagColor(poi);
      const borderColor = selected
        ? mapColors.selectedStroke
        : mapColors.iconFill;
      const imageId = getMarkerImageId(
        parsed.name,
        backgroundColor,
        borderColor,
        selected,
      );
      images.set(imageId, {
        imageId,
        url: getPhosphorIconUrl(parsed.name),
        backgroundColor,
        borderColor,
        selected,
      });
    }
    return Array.from(images.values());
  }, [
    poisWithCoords,
    getEffectiveTag,
    getEffectiveTagColor,
    mapColors.iconFill,
    mapColors.selectedFill,
    mapColors.selectedStroke,
    selectedPoiId,
  ]);

  // Build POI lookup map
  const poiMap = useMemo(() => {
    const map = new Map<string, POI & { lat: number; lng: number }>();
    for (const poi of poisWithCoords) {
      map.set(poi.id, poi);
    }
    return map;
  }, [poisWithCoords]);

  // Keep poiMap ref in sync for event handlers
  poiMapRef.current = poiMap;

  // Convert POIs to GeoJSON FeatureCollection
  const geojsonData = useMemo(
    () => ({
      type: "FeatureCollection" as const,
      features: poisWithCoords.map((poi) => ({
        type: "Feature" as const,
        geometry: {
          type: "Point" as const,
          coordinates: [poi.lng, poi.lat],
        },
        properties: {
          id: poi.id,
          name: poi.name,
          tags: getTagNames(poi).join(", "),
          color: getEffectiveTagColor(poi),
          iconImage: getEffectiveMarkerImage(poi),
          isSelected: poi.id === selectedPoiId,
          isRoutePoi: routePoiIdSet.has(poi.id),
          hasCustomIcon: hasCustomIcon(poi),
          ...resultNumberProperty(resultNumberByPoiId, poi.id),
        },
      })),
    }),
    [
      poisWithCoords,
      getEffectiveTagColor,
      getEffectiveMarkerImage,
      getTagNames,
      selectedPoiId,
      routePoiIdSet,
      hasCustomIcon,
      resultNumberByPoiId,
    ],
  );
  const geojsonDataRef = useRef(geojsonData);
  geojsonDataRef.current = geojsonData;

  const routePoiGeojson = useMemo(
    () => ({
      type: "FeatureCollection" as const,
      features: routePoiEntries
        .map((entry, index) => {
          const poi = poiMap.get(entry.poiId);
          if (!poi) return null;

          const routeOrder = routeOrderByPoiId?.get(entry.poiId) ?? index + 1;
          return {
            type: "Feature" as const,
            geometry: {
              type: "Point" as const,
              coordinates: entry.coord,
            },
            properties: {
              id: poi.id,
              name: poi.name,
              tags: getTagNames(poi).join(", "),
              color: getEffectiveTagColor(poi),
              iconImage: getEffectiveMarkerImage(poi),
              isSelected: poi.id === selectedPoiId,
              routeOrder,
              hasCustomIcon: hasCustomIcon(poi),
              ...resultNumberProperty(resultNumberByPoiId, poi.id),
            },
          };
        })
        .filter(
          (feature): feature is NonNullable<typeof feature> => feature !== null,
        ),
    }),
    [
      routePoiEntries,
      routeOrderByPoiId,
      poiMap,
      getEffectiveTagColor,
      getEffectiveMarkerImage,
      getTagNames,
      selectedPoiId,
      hasCustomIcon,
      resultNumberByPoiId,
    ],
  );
  const routePoiGeojsonRef = useRef(routePoiGeojson);
  routePoiGeojsonRef.current = routePoiGeojson;

  // Report bounds changes (debounced)
  const reportBounds = useCallback(() => {
    if (!onBoundsChange || !mapRef.current) return;

    if (boundsTimeoutRef.current) {
      window.clearTimeout(boundsTimeoutRef.current);
    }

    boundsTimeoutRef.current = window.setTimeout(() => {
      const map = mapRef.current;
      if (!map) return;

      const bounds = map.getBounds();
      if (!bounds) return;

      onBoundsChange({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest(),
      });
    }, 150);
  }, [onBoundsChange]);

  const fitMapToPois = useCallback((targetPois?: POI[]) => {
    const map = mapRef.current;
    if (!map || !mapLoadedRef.current) return false;

    const fitPois = (targetPois ?? fitToPoisRef.current ?? []).filter(
      (poi): poi is POI & { lat: number; lng: number } =>
        poi.lat != null &&
        poi.lng != null &&
        !isNaN(poi.lat) &&
        !isNaN(poi.lng),
    );

    if (fitPois.length === 0) return false;

    if (fitPois.length === 1) {
      const poi = fitPois[0];
      map.easeTo({
        center: [poi.lng, poi.lat],
        zoom: zoomLockedRef.current ? map.getZoom() : 13,
        duration: 500,
      });
      hasInitialFitRef.current = true;
      return true;
    }

    const bounds = new mapboxgl.LngLatBounds();
    fitPois.forEach((poi) => {
      bounds.extend([poi.lng, poi.lat]);
    });

    if (zoomLockedRef.current) {
      const camera = map.cameraForBounds(bounds, { padding: 80 });
      if (camera?.center) {
        map.easeTo({ center: camera.center, duration: 500 });
      }
    } else {
      map.fitBounds(bounds, { padding: 80, maxZoom: 14, duration: 500 });
    }
    hasInitialFitRef.current = true;
    return true;
  }, []);

  // Initialize map and add layers
  useEffect(() => {
    if (!mapContainerRef.current || !mapboxApiKey) return;

    mapboxgl.accessToken = mapboxApiKey;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: mapStyleToUrl(mapStyle),
      center: [0, 20],
      zoom: 2,
    });
    appliedMapStyleRef.current = mapStyle;

    map.addControl(new mapboxgl.NavigationControl(), "top-right");

    const zoomLockControl: mapboxgl.IControl = {
      onAdd: () => {
        const node = zoomLockControlNode ?? document.createElement("div");
        node.className = "mapboxgl-ctrl mapboxgl-ctrl-group";
        return node;
      },
      onRemove: () => {
        zoomLockControlNode?.remove();
      },
    };
    map.addControl(zoomLockControl, "top-right");

    const geolocateControl = new mapboxgl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: true,
      showUserHeading: true,
    });
    // Permission-denied and unsupported-browser cases surface here instead
    // of as an unhandled error; the control itself already shows its own
    // inactive state, so this only needs to avoid a console exception.
    geolocateControl.on("error", (err: GeolocationPositionError) => {
      console.warn("Geolocation error", err);
    });
    map.addControl(geolocateControl, "top-right");
    map.addControl(new mapboxgl.FullscreenControl(), "top-right");
    map.addControl(new mapboxgl.ScaleControl());

    const scheduleResize = () => {
      if (resizeFrameRef.current != null) return;
      resizeFrameRef.current = window.requestAnimationFrame(() => {
        resizeFrameRef.current = null;
        const currentMap = mapRef.current;
        if (!currentMap) return;
        currentMap.resize();
      });
    };

    // Create popup instance (reused for hover)
    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 15,
    });
    popupRef.current = popup;

    // Separate popup for basemap points of interest: click-persistent, with
    // its own close button, and React-rendered content (see the portal below).
    const basemapPopup = new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: true,
      offset: 15,
      maxWidth: "260px",
    });
    if (basemapPopupNode) {
      basemapPopup.setDOMContent(basemapPopupNode);
    }
    basemapPopup.on("close", () => setBasemapPlace(null));
    basemapPopupRef.current = basemapPopup;

    // True when the pointer is over one of our own markers, which always win
    // over the basemap labels drawn beneath them.
    const isOverAppMarker = (point: mapboxgl.Point) => {
      const layers = APP_INTERACTIVE_LAYERS.filter((layerId) =>
        map.getLayer(layerId),
      );
      if (layers.length === 0) return false;
      return map.queryRenderedFeatures(point, { layers }).length > 0;
    };

    // Basemap POI labels belong to the imported Standard style, so they are
    // only reachable through the Interactions API rather than map.on(type,
    // layerId). Registered on the map (not the style) so they survive a style
    // switch; both Standard styles publish the "poi" featureset.
    const basemapPoiTarget = {
      featuresetId: BASEMAP_POI_FEATURESET_ID,
      importId: BASEMAP_IMPORT_ID,
    };

    map.addInteraction(BASEMAP_POI_CLICK_INTERACTION, {
      type: "click",
      target: basemapPoiTarget,
      handler: (e) => {
        if (isOverAppMarker(e.point)) return false;

        const place = basemapPlaceFromFeature(e.feature);
        if (!place) return false;

        // Interactions dispatch through an ordinary map click listener, so the
        // background-click handler still runs; this lets it recognise the same
        // click and leave the current selection alone.
        basemapPlaceClickTimestampRef.current = e.originalEvent.timeStamp;
        // The popup itself is attached by the effect below, once its React
        // content has been committed and Mapbox can measure it.
        setBasemapPlace(place);
      },
    });

    map.addInteraction(BASEMAP_POI_ENTER_INTERACTION, {
      type: "mouseenter",
      target: basemapPoiTarget,
      handler: (e) => {
        if (isOverAppMarker(e.point)) return false;
        map.getCanvas().style.cursor = "pointer";
      },
    });

    map.addInteraction(BASEMAP_POI_LEAVE_INTERACTION, {
      type: "mouseleave",
      target: basemapPoiTarget,
      handler: () => {
        map.getCanvas().style.cursor = "";
      },
    });

    // Registers POI/cluster/route sources, layers, and interaction handlers.
    // Mapbox drops all custom sources/layers/images on setStyle, so this must
    // be re-run (idempotently, against a freshly loaded style) on every
    // "style.load", not just the initial "load".
    const setupMapLayers = () => {
      // Add clustered POI source
      map.addSource(SOURCE_ID, {
        type: "geojson",
        data: geojsonDataRef.current,
        cluster: true,
        clusterMaxZoom: 12,
        clusterRadius: 30,
        clusterMinPoints: 2,
      });

      map.addSource(ROUTE_POI_SOURCE_ID, {
        type: "geojson",
        data: routePoiGeojsonRef.current,
      });

      // Cluster circles
      map.addLayer({
        id: CLUSTER_LAYER,
        type: "circle",
        source: SOURCE_ID,
        filter: ["has", "point_count"],
        paint: {
          "circle-color": mapColors.defaultMarker,
          "circle-radius": [
            "step",
            ["get", "point_count"],
            18,
            10,
            22,
            50,
            26,
            100,
            30,
          ],
          "circle-stroke-width": 3,
          "circle-stroke-color": "white",
        },
      });

      // Cluster count labels
      map.addLayer({
        id: CLUSTER_COUNT_LAYER,
        type: "symbol",
        source: SOURCE_ID,
        filter: ["has", "point_count"],
        layout: {
          "text-field": "{point_count_abbreviated}",
          "text-font": ["DIN Pro Medium", "Arial Unicode MS Bold"],
          "text-size": 12,
        },
        paint: {
          "text-color": "white",
        },
      });

      // The marker background and glyph are precomposed into one runtime image.
      // Keeping them in one symbol layer makes overlapping markers paint as
      // complete units instead of placing every background beneath every glyph.
      map.addLayer({
        id: POI_SYMBOL_LAYER,
        type: "symbol",
        source: SOURCE_ID,
        filter: [
          "all",
          ["!", ["has", "point_count"]],
          ["!=", ["get", "isRoutePoi"], true],
        ],
        layout: {
          "icon-image": ["get", "iconImage"],
          // Custom icons render larger since they ARE the marker (no circle behind)
          "icon-size": [
            "case",
            ["==", ["get", "hasCustomIcon"], true],
            ["case", ["==", ["get", "isSelected"], true], 1.92, 1.56],
            1,
          ],
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
        },
        paint: {
          "icon-opacity": 1,
        },
      });

      map.addLayer({
        id: ROUTE_POI_SYMBOL_LAYER,
        type: "symbol",
        source: ROUTE_POI_SOURCE_ID,
        layout: {
          "icon-image": ["get", "iconImage"],
          // Custom icons render larger since they ARE the marker (no circle behind)
          "icon-size": [
            "case",
            ["==", ["get", "hasCustomIcon"], true],
            ["case", ["==", ["get", "isSelected"], true], 1.92, 1.56],
            1,
          ],
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
        },
        paint: {
          "icon-opacity": 1,
        },
      });

      map.addLayer({
        id: ROUTE_POI_ORDER_LAYER,
        type: "symbol",
        source: ROUTE_POI_SOURCE_ID,
        layout: {
          "text-field": ["to-string", ["get", "routeOrder"]],
          "text-font": ["DIN Pro Bold", "Arial Unicode MS Bold"],
          "text-size": 13,
          "text-offset": [0.9, -0.9],
          "text-allow-overlap": true,
          "text-ignore-placement": true,
        },
        paint: {
          "text-color": "white",
          "text-halo-color": mapColors.route,
          "text-halo-width": 8,
        },
      });

      // Results numbers sit on the opposite corner from route order badges,
      // so a planned place that is also a result shows both.
      const resultNumberLayout = {
        "text-field": ["to-string", ["get", "resultNumber"]],
        "text-font": ["DIN Pro Bold", "Arial Unicode MS Bold"],
        "text-size": 12,
        "text-offset": [-0.9, -0.9],
        "text-allow-overlap": true,
        "text-ignore-placement": true,
      } satisfies mapboxgl.SymbolLayerSpecification["layout"];
      const resultNumberPaint = {
        "text-color": "white",
        "text-halo-color": mapColors.resultBadge,
        "text-halo-width": 7,
      } satisfies mapboxgl.SymbolLayerSpecification["paint"];
      map.addLayer({
        id: POI_RESULT_NUMBER_LAYER,
        type: "symbol",
        source: SOURCE_ID,
        filter: [
          "all",
          ["!", ["has", "point_count"]],
          ["!=", ["get", "isRoutePoi"], true],
          ["has", "resultNumber"],
        ],
        layout: resultNumberLayout,
        paint: resultNumberPaint,
      });
      map.addLayer({
        id: ROUTE_POI_RESULT_NUMBER_LAYER,
        type: "symbol",
        source: ROUTE_POI_SOURCE_ID,
        filter: ["has", "resultNumber"],
        layout: resultNumberLayout,
        paint: resultNumberPaint,
      });

      // Click on cluster -> zoom in
      map.on("click", CLUSTER_LAYER, (e) => {
        const features = map.queryRenderedFeatures(e.point, {
          layers: [CLUSTER_LAYER],
        });
        if (!features.length) return;

        const clusterId = features[0].properties?.cluster_id;
        const source = map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource;

        source.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;

          const geometry = features[0].geometry;
          if (geometry.type === "Point") {
            map.easeTo({
              center: geometry.coordinates as [number, number],
              zoom: zoom ?? 14,
            });
          }
        });
      });

      const poiInteractiveLayers = [
        POI_SYMBOL_LAYER,
        ROUTE_POI_SYMBOL_LAYER,
        ROUTE_POI_ORDER_LAYER,
        POI_RESULT_NUMBER_LAYER,
        ROUTE_POI_RESULT_NUMBER_LAYER,
      ] as const;

      const handlePoiClick = (e: mapboxgl.MapLayerMouseEvent) => {
        const ts = e.originalEvent.timeStamp;
        if (ts === lastPoiClickTimestampRef.current) return;
        lastPoiClickTimestampRef.current = ts;

        const poiId = e.features?.[0]?.properties?.id as string | undefined;
        if (!poiId) return;

        const currentSelected = selectedPoiIdRef.current;
        onSelectPoiRef.current(poiId === currentSelected ? null : poiId);
      };

      const handlePoiHover = (e: mapboxgl.MapLayerMouseEvent) => {
        const feature = e.features?.[0];
        const poiId = e.features?.[0]?.properties?.id as string | undefined;
        if (!poiId) return;

        const poi = poiMapRef.current.get(poiId);
        if (!poi) return;

        const coords =
          feature?.geometry.type === "Point"
            ? (feature.geometry.coordinates as [number, number])
            : ([poi.lng, poi.lat] as [number, number]);

        popup
          .setLngLat(coords)
          .setHTML(
            `
            <div style="font-family: system-ui, sans-serif; padding: 4px 0;">
              <div style="font-weight: 600; font-size: 13px; color: ${mapColors.popupTitle};">${poi.name}</div>
              ${getTagNamesRef.current(poi).length > 0 ? `<div style="font-size: 11px; color: ${mapColors.popupMeta}; margin-top: 2px;">${getTagNamesRef.current(poi).join(", ")}</div>` : ""}
            </div>
          `,
          )
          .addTo(map);
      };

      for (const layerId of poiInteractiveLayers) {
        map.on("click", layerId, handlePoiClick);
        map.on("mouseenter", layerId, () => {
          map.getCanvas().style.cursor = "pointer";
        });
        map.on("mousemove", layerId, handlePoiHover);
        map.on("mouseleave", layerId, () => {
          map.getCanvas().style.cursor = "";
          popup.remove();
        });
      }

      // Cluster hover: cursor + popup listing member POI names
      map.on("mouseenter", CLUSTER_LAYER, () => {
        map.getCanvas().style.cursor = "pointer";
      });

      const handleClusterHover = (e: mapboxgl.MapLayerMouseEvent) => {
        const feature = e.features?.[0];
        const clusterId = feature?.properties?.cluster_id as number | undefined;
        const pointCount = feature?.properties?.point_count as
          | number
          | undefined;
        if (clusterId == null || feature?.geometry.type !== "Point") return;

        const coords = feature.geometry.coordinates as [number, number];

        if (lastHoveredClusterIdRef.current === clusterId) {
          popup.setLngLat(coords);
          return;
        }
        lastHoveredClusterIdRef.current = clusterId;

        const requestId = ++clusterHoverRequestIdRef.current;
        const source = map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource;

        source.getClusterLeaves(
          clusterId,
          CLUSTER_POPUP_MAX_NAMES + 1,
          0,
          (err, leaves) => {
            if (err || requestId !== clusterHoverRequestIdRef.current) return;

            const items = (leaves ?? [])
              .slice(0, CLUSTER_POPUP_MAX_NAMES)
              .map((leaf) => {
                const poiId = leaf.properties?.id as string | undefined;
                const name = leaf.properties?.name as string | undefined;
                if (!poiId || !name) return null;

                const poi = poiMapRef.current.get(poiId);
                const iconUrl = poi
                  ? getEffectivePoiIconUrlRef.current(poi)
                  : null;
                return { name, iconUrl };
              })
              .filter(
                (item): item is { name: string; iconUrl: string | null } =>
                  item !== null,
              );
            const remaining = (pointCount ?? items.length) - items.length;

            popup
              .setLngLat(coords)
              .setHTML(
                `
                <div style="font-family: system-ui, sans-serif; padding: 4px 0;">
                  ${items
                    .map(
                      (item) => `
                      <div style="display: flex; align-items: center; gap: 6px; font-size: 12px; color: ${mapColors.popupTitle};">
                        ${
                          item.iconUrl
                            ? `<img src="${item.iconUrl}" alt="" style="width: 14px; height: 14px; object-fit: contain; flex-shrink: 0;" />`
                            : ""
                        }
                        <span>${item.name}</span>
                      </div>
                    `,
                    )
                    .join("")}
                  ${
                    remaining > 0
                      ? `<div style="font-size: 11px; color: ${mapColors.popupMeta}; margin-top: 2px;">and ${remaining} more</div>`
                      : ""
                  }
                </div>
              `,
              )
              .addTo(map);
          },
        );
      };

      map.on("mousemove", CLUSTER_LAYER, handleClusterHover);

      map.on("mouseleave", CLUSTER_LAYER, () => {
        map.getCanvas().style.cursor = "";
        popup.remove();
        lastHoveredClusterIdRef.current = null;
        clusterHoverRequestIdRef.current += 1;
      });

      // Click on map background -> deselect
      map.on("click", (e) => {
        // A click consumed by the basemap point-of-interest interaction is not
        // a background click; opening that popup keeps the current selection.
        if (
          basemapPlaceClickTimestampRef.current === e.originalEvent.timeStamp
        ) {
          return;
        }
        const hitFeatures = map.queryRenderedFeatures(e.point, {
          layers: [...APP_INTERACTIVE_LAYERS],
        });
        if (hitFeatures.length > 0) return;
        onSelectPoiRef.current(null);
      });
    };

    map.on("load", () => {
      mapLoadedRef.current = true;
      setMapReadyVersion((v) => v + 1);

      setupMapLayers();
      setLayersReadyVersion((v) => v + 1);

      // Ensure canvas dimensions match current layout before interaction.
      scheduleResize();
      window.setTimeout(scheduleResize, 0);

      // Report initial bounds
      reportBounds();

      // Registered after the initial load so it only fires on subsequent
      // style switches (via setStyle), never for the first style load.
      map.on("style.load", () => {
        setupMapLayers();
        mapLoadedRef.current = true;
        setLayersReadyVersion((v) => v + 1);
        setStyleSwitching(false);
      });
    });

    map.on("moveend", reportBounds);

    const handleWindowResize = () => {
      scheduleResize();
      reportBounds();
    };
    window.addEventListener("resize", handleWindowResize);

    const resizeObserver =
      typeof ResizeObserver !== "undefined" && mapContainerRef.current
        ? new ResizeObserver(() => {
            scheduleResize();
            reportBounds();
          })
        : null;

    if (resizeObserver && mapContainerRef.current) {
      resizeObserver.observe(mapContainerRef.current);
    }

    mapRef.current = map;
    const customImageData = customImageDataRef.current;
    const markerImageSignatures = markerImageSignatureRef.current;

    return () => {
      if (boundsTimeoutRef.current) {
        window.clearTimeout(boundsTimeoutRef.current);
      }
      if (resizeFrameRef.current != null) {
        window.cancelAnimationFrame(resizeFrameRef.current);
        resizeFrameRef.current = null;
      }

      window.removeEventListener("resize", handleWindowResize);
      resizeObserver?.disconnect();

      popup.remove();
      basemapPopup.remove();
      map.remove();

      mapRef.current = null;
      popupRef.current = null;
      basemapPopupRef.current = null;
      basemapPlaceClickTimestampRef.current = null;
      mapLoadedRef.current = false;
      customImageData.clear();
      markerImageSignatures.clear();
      setMapReadyVersion(0);
      setLayersReadyVersion(0);
      appliedMapStyleRef.current = null;
      setStyleSwitching(false);
      hasInitialFitRef.current = false;
      lastFlyToPoiRef.current = null;
      lastFlyToTriggerRef.current = undefined;
      lastFitToPoisKeyRef.current = undefined;
      lastFitToPoisSignatureRef.current = null;
      lastMapReadyVersionRef.current = 0;
    };
  }, [mapboxApiKey, reportBounds, basemapPopupNode, zoomLockControlNode]);

  // Lock/unlock the gesture-driven zoom handlers. The Navigation control's
  // zoom buttons call map.zoomIn/zoomOut directly and are intentionally left
  // working as a deliberate override while locked.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoadedRef.current) return;

    if (zoomLocked) {
      map.scrollZoom.disable();
      map.boxZoom.disable();
      map.doubleClickZoom.disable();
      map.touchZoomRotate.disable();
    } else {
      map.scrollZoom.enable();
      map.boxZoom.enable();
      map.doubleClickZoom.enable();
      map.touchZoomRotate.enable();
    }
  }, [zoomLocked, mapReadyVersion]);

  // Update POI data when it changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoadedRef.current) return;

    const source = map.getSource(SOURCE_ID) as
      | mapboxgl.GeoJSONSource
      | undefined;
    if (source) {
      source.setData(geojsonData);
    }

    // Fit bounds on initial load only when no explicit fit target is driving the viewport.
    if (fitToPoisRef.current?.length) return;

    if (
      poisWithCoords.length > 0 &&
      !selectedPoiId &&
      !hasInitialFitRef.current
    ) {
      const bounds = new mapboxgl.LngLatBounds();
      poisWithCoords.forEach((poi) => {
        bounds.extend([poi.lng, poi.lat]);
      });
      map.fitBounds(bounds, { padding: 60, maxZoom: 14 });
      hasInitialFitRef.current = true;
    }
  }, [geojsonData, poisWithCoords, selectedPoiId]);

  // Update route POI data (non-clustered, geolocked) when route context changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoadedRef.current) return;

    const source = map.getSource(ROUTE_POI_SOURCE_ID) as
      | mapboxgl.GeoJSONSource
      | undefined;
    if (source) {
      source.setData(routePoiGeojson);
    }
  }, [routePoiGeojson]);

  // Keep runtime custom PNGs and precomposed standard markers in sync.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoadedRef.current) return;

    let cancelled = false;
    const activeCustomIds = new Set(customTagIcons.map((icon) => icon.imageId));
    const activeMarkerIds = new Set(
      standardMarkerImages.map((image) => image.imageId),
    );

    for (const [imageId] of customImageDataRef.current) {
      if (activeCustomIds.has(imageId)) continue;
      if (map.hasImage(imageId)) {
        map.removeImage(imageId);
      }
      customImageDataRef.current.delete(imageId);
    }

    for (const [imageId] of markerImageSignatureRef.current) {
      if (activeMarkerIds.has(imageId)) continue;
      if (map.hasImage(imageId)) {
        map.removeImage(imageId);
      }
      markerImageSignatureRef.current.delete(imageId);
    }

    for (const { imageId, dataUrl } of customTagIcons) {
      const previousData = customImageDataRef.current.get(imageId);
      if (previousData === dataUrl && map.hasImage(imageId)) continue;

      void loadImageFromDataUrl(dataUrl)
        .then((imageData) => {
          if (cancelled) return;
          if (map.hasImage(imageId)) {
            map.removeImage(imageId);
          }
          map.addImage(imageId, imageData, {
            pixelRatio: PHOSPHOR_RUNTIME_ICON_PIXEL_RATIO,
          });
          customImageDataRef.current.set(imageId, dataUrl);
        })
        .catch((err) => {
          console.warn(`Failed to load custom tag icon ${imageId}`, err);
        });
    }

    for (const markerImage of standardMarkerImages) {
      const { imageId, url, backgroundColor, borderColor, selected } =
        markerImage;
      const signature = `${url}|${backgroundColor}|${borderColor}|${selected}`;
      const previousSignature = markerImageSignatureRef.current.get(imageId);
      if (previousSignature === signature && map.hasImage(imageId)) continue;

      void loadMarkerImageData(markerImage, mapColors.iconFill)
        .then((markerImageData) => {
          if (cancelled) return;
          if (map.hasImage(imageId)) {
            map.removeImage(imageId);
          }

          map.addImage(imageId, markerImageData, {
            pixelRatio: PHOSPHOR_RUNTIME_ICON_PIXEL_RATIO,
          });
          markerImageSignatureRef.current.set(imageId, signature);
        })
        .catch((err) => {
          console.warn(`Failed to load marker image ${imageId}`, err);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [
    customTagIcons,
    standardMarkerImages,
    layersReadyVersion,
    mapColors.iconFill,
  ]);

  // Switch between Standard style variants. Mapbox does not preserve custom
  // sources/layers/images/config across setStyle, so the "style.load"
  // handler re-runs setupMapLayers() and bumps layersReadyVersion; the
  // camera itself is preserved by Mapbox across setStyle, so this
  // intentionally does not touch mapReadyVersion (see its declaration).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoadedRef.current) return;
    if (appliedMapStyleRef.current === mapStyle) return;

    appliedMapStyleRef.current = mapStyle;
    mapLoadedRef.current = false;
    setStyleSwitching(true);
    map.setStyle(mapStyleToUrl(mapStyle));
  }, [mapStyle]);

  // Enable/disable 3D terrain exaggeration. The DEM source is added lazily
  // on first enable and, like everything else, is wiped by setStyle, so
  // `map.getSource` (not a ref) is the source of truth for whether it needs
  // re-adding; keyed on layersReadyVersion to reapply if terrain was left
  // enabled through a style switch.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoadedRef.current) return;

    if (!terrainEnabled) {
      map.setTerrain(null);
      return;
    }

    if (!map.getSource(TERRAIN_SOURCE_ID)) {
      map.addSource(TERRAIN_SOURCE_ID, {
        type: "raster-dem",
        url: "mapbox://mapbox.mapbox-terrain-dem-v1",
        tileSize: 512,
        maxzoom: 14,
      });
    }

    map.setTerrain({
      source: TERRAIN_SOURCE_ID,
      exaggeration: TERRAIN_EXAGGERATION,
    });

    if (map.getPitch() === 0) {
      map.easeTo({ pitch: 60, duration: 600 });
    }
  }, [terrainEnabled, layersReadyVersion]);

  // Apply Standard style config properties (label toggles, 3D buildings,
  // light preset). Keyed on layersReadyVersion so these are reapplied
  // whenever the map (re)loads a style, since Standard resets config
  // properties on every style load.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoadedRef.current) return;

    map.setConfigProperty("basemap", "showTransitLabels", showTransitLabels);
    map.setConfigProperty(
      "basemap",
      "showPointOfInterestLabels",
      showPointOfInterestLabels,
    );
    map.setConfigProperty("basemap", "showPlaceLabels", showPlaceLabels);
    map.setConfigProperty("basemap", "showRoadLabels", showRoadLabels);
    map.setConfigProperty("basemap", "show3dObjects", show3dObjects);
    map.setConfigProperty("basemap", "lightPreset", lightPreset);
  }, [
    showTransitLabels,
    showPointOfInterestLabels,
    showPlaceLabels,
    showRoadLabels,
    show3dObjects,
    lightPreset,
    layersReadyVersion,
  ]);

  // Runs before the viewport intent below, so a fit or fly on load already
  // frames the space left between the overlays. Later changes (the results
  // card resizing) glide, unless the camera is mid-animation.
  const insetTop = viewportInsets?.top ?? 0;
  const insetBottom = viewportInsets?.bottom ?? 0;
  const paddedMapVersionRef = useRef(0);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoadedRef.current) return;
    const firstForMap = paddedMapVersionRef.current !== mapReadyVersion;
    paddedMapVersionRef.current = mapReadyVersion;
    const current = map.getPadding();
    if (current.top === insetTop && current.bottom === insetBottom) return;
    const padding = { top: insetTop, bottom: insetBottom, left: 0, right: 0 };
    if (firstForMap || map.isMoving()) map.setPadding(padding);
    else map.easeTo({ padding, duration: 250 });
  }, [insetTop, insetBottom, mapReadyVersion]);

  // Apply one viewport intent per render. A fly request must never be followed
  // by contextual fitBounds in the same update. Deselecting a POI holds the
  // current viewport rather than re-fitting to every POI.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoadedRef.current) return;

    const intent = resolveMapViewportIntent({
      selectedPoiId,
      flyToTrigger,
      lastFlyToPoiId: lastFlyToPoiRef.current,
      lastFlyToTrigger: lastFlyToTriggerRef.current,
      fitToPoisKey,
      lastFitToPoisKey: lastFitToPoisKeyRef.current,
      fitToPoisSignature,
      lastFitToPoisSignature: lastFitToPoisSignatureRef.current,
      mapReadyVersion,
      lastMapReadyVersion: lastMapReadyVersionRef.current,
    });

    if (intent === "fly" && selectedPoiId) {
      const selectedPoi = pois.find((poi) => poi.id === selectedPoiId);
      if (
        selectedPoi?.lat != null &&
        selectedPoi.lng != null &&
        Number.isFinite(selectedPoi.lat) &&
        Number.isFinite(selectedPoi.lng)
      ) {
        map.flyTo({
          center: [selectedPoi.lng, selectedPoi.lat],
          zoom: zoomLockedRef.current
            ? map.getZoom()
            : Math.max(map.getZoom(), 14),
          duration: 1000,
        });
      }
      lastFlyToPoiRef.current = selectedPoiId;
      lastFlyToTriggerRef.current = flyToTrigger;
    } else if (intent === "fit") {
      map.resize();
      fitMapToPois(fitToPois);
    }

    if (!selectedPoiId) {
      lastFlyToPoiRef.current = null;
      lastFlyToTriggerRef.current = flyToTrigger;
    }
    lastFitToPoisKeyRef.current = fitToPoisKey;
    lastFitToPoisSignatureRef.current = fitToPoisSignature;
    lastMapReadyVersionRef.current = mapReadyVersion;
  }, [
    selectedPoiId,
    flyToTrigger,
    pois,
    fitToPois,
    fitToPoisKey,
    fitToPoisSignature,
    fitMapToPois,
    mapReadyVersion,
  ]);

  // Manage route line source/layer
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoadedRef.current) return;

    // Remove existing route layer and source if present
    if (map.getLayer(ROUTE_LAYER_ID)) {
      map.removeLayer(ROUTE_LAYER_ID);
    }
    if (map.getSource(ROUTE_SOURCE_ID)) {
      map.removeSource(ROUTE_SOURCE_ID);
    }

    // If no route line, we're done
    const drawableRoutes = effectiveRouteLines.filter(
      (line) => line.coordinates.length >= 2,
    );
    if (drawableRoutes.length === 0) return;

    // Add route line source
    map.addSource(ROUTE_SOURCE_ID, {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: drawableRoutes.map((line) => ({
          type: "Feature" as const,
          properties: { date: line.date ?? "" },
          geometry: {
            type: "LineString" as const,
            coordinates: line.coordinates,
          },
        })),
      },
    });

    // Add route line layer (below POI markers)
    map.addLayer(
      {
        id: ROUTE_LAYER_ID,
        type: "line",
        source: ROUTE_SOURCE_ID,
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": mapColors.route,
          "line-width": 3,
          "line-dasharray": [2, 2],
          "line-opacity": 0.7,
        },
      },
      CLUSTER_LAYER,
    ); // Insert below cluster layer
  }, [effectiveRouteLines, layersReadyVersion, mapColors.route]);

  // Attach/detach the basemap popup from the selected place. Running here
  // rather than in the click handler means the popup is measured against its
  // rendered content, and every way of clearing the place (close button,
  // closeOnClick, adding to the library) closes it the same way.
  useEffect(() => {
    const popup = basemapPopupRef.current;
    if (!popup) return;

    const map = mapRef.current;
    if (!basemapPlace || !map) {
      popup.remove();
      return;
    }

    popup.setLngLat([basemapPlace.lng, basemapPlace.lat]).addTo(map);
  }, [basemapPlace]);

  const handleAddBasemapPlaceToLibrary = useCallback((place: BasemapPlace) => {
    setBasemapPlace(null);
    useUIStore.getState().openPOIEditor("create", undefined, {
      name: place.name,
      lat: place.lat,
      lng: place.lng,
      googleMapsUrl: basemapPlaceMapsUrl(place),
    });
  }, []);

  // No API key configured
  if (!mapboxApiKey) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-slate-100 rounded-xl border border-border">
        <span className="material-symbols-outlined text-5xl text-slate-300 mb-4">
          map
        </span>
        <h3 className="text-base font-bold text-primary mb-2">
          Map not configured
        </h3>
        <p className="text-sm text-secondary text-center max-w-xs mb-4">
          Add your Mapbox API key in Settings to display the map.
        </p>
        <a href="/settings" className="btn-primary text-sm">
          Go to Settings
        </a>
      </div>
    );
  }

  const showEmptyState = poisWithCoords.length === 0;

  return (
    <div
      ref={mapContainerRef}
      className={clsx(
        "flex-1 h-full overflow-hidden",
        viewportInsets ? "map-insets" : "rounded-xl border border-border",
      )}
      style={
        {
          minHeight: "300px",
          position: "relative",
          "--map-inset-top": `${insetTop}px`,
          "--map-inset-bottom": `${insetBottom}px`,
        } as CSSProperties
      }
    >
      {showEmptyState && (
        <div
          className="flex flex-col items-center justify-center bg-slate-100/95"
          style={{ position: "absolute", inset: 0, zIndex: 10 }}
        >
          <span className="material-symbols-outlined text-5xl text-slate-300 mb-4">
            location_off
          </span>
          <h3 className="text-base font-bold text-primary mb-2">
            No locations to display
          </h3>
          <p className="text-sm text-secondary text-center max-w-xs">
            {pois.length === 0
              ? "Create some POIs to see them on the map."
              : "None of your POIs have coordinates yet."}
          </p>
        </div>
      )}
      <MapLayerControl
        mapStyle={mapStyle}
        onMapStyleChange={setMapStyle}
        styleSwitching={styleSwitching}
        showTransitLabels={showTransitLabels}
        onShowTransitLabelsChange={setShowTransitLabels}
        showPointOfInterestLabels={showPointOfInterestLabels}
        onShowPointOfInterestLabelsChange={setShowPointOfInterestLabels}
        showPlaceLabels={showPlaceLabels}
        onShowPlaceLabelsChange={setShowPlaceLabels}
        showRoadLabels={showRoadLabels}
        onShowRoadLabelsChange={setShowRoadLabels}
        lightPreset={lightPreset}
        onLightPresetChange={setLightPreset}
        show3dObjects={show3dObjects}
        onShow3dObjectsChange={setShow3dObjects}
        terrainEnabled={terrainEnabled}
        onTerrainEnabledChange={setTerrainEnabled}
      />
      {basemapPlace &&
        basemapPopupNode &&
        createPortal(
          <BasemapPlacePopup
            place={basemapPlace}
            onAddToLibrary={handleAddBasemapPlaceToLibrary}
          />,
          basemapPopupNode,
        )}
      {zoomLockControlNode &&
        createPortal(
          <Button
            type="button"
            variant="icon"
            size="md"
            selected={zoomLocked}
            aria-pressed={zoomLocked}
            aria-label={zoomLocked ? "Unlock zoom" : "Lock zoom"}
            title={
              zoomLocked
                ? "Zoom locked — scroll and pinch won't change it"
                : "Lock zoom level"
            }
            icon={zoomLocked ? "lock" : "lock_open"}
            onClick={() => setZoomLocked((locked) => !locked)}
          />,
          zoomLockControlNode,
        )}
    </div>
  );
}

function loadImageFromDataUrl(dataUrl: string): Promise<ImageData> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        resolve(rasterizeCustomIconImage(img));
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => reject(new Error("Failed to load data URL image"));
    img.src = dataUrl;
  });
}

function rasterizeCustomIconImage(img: HTMLImageElement): ImageData {
  const logicalSize = PHOSPHOR_RUNTIME_ICON_SIZE;
  const pixelRatio = PHOSPHOR_RUNTIME_ICON_PIXEL_RATIO;
  const size = logicalSize * pixelRatio;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Failed to get 2d context for custom icon rasterization");
  }

  // Custom icons fill the full canvas (no padding) since they ARE the marker
  const scale = Math.min(size / img.naturalWidth, size / img.naturalHeight);
  const width = Math.max(1, Math.round(img.naturalWidth * scale));
  const height = Math.max(1, Math.round(img.naturalHeight * scale));
  const x = Math.floor((size - width) / 2);
  const y = Math.floor((size - height) / 2);

  ctx.clearRect(0, 0, size, size);
  ctx.drawImage(img, x, y, width, height);

  return ctx.getImageData(0, 0, size, size);
}

function loadImageFromUrl(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image URL: ${url}`));
    img.src = url;
  });
}

interface MarkerImageDefinition {
  imageId: string;
  url: string;
  backgroundColor: string;
  borderColor: string;
  selected: boolean;
}

function getMarkerImageId(
  iconName: string,
  backgroundColor: string,
  borderColor: string,
  selected: boolean,
): string {
  return [
    "poi-marker",
    iconName,
    encodeURIComponent(backgroundColor),
    encodeURIComponent(borderColor),
    selected ? "selected" : "normal",
  ].join(":");
}

async function loadMarkerImageData(
  marker: MarkerImageDefinition,
  glyphColor: string,
): Promise<ImageData> {
  const img = await loadImageFromUrl(marker.url);
  return rasterizeMarkerImage(img, marker, glyphColor);
}

function rasterizeMarkerImage(
  img: HTMLImageElement,
  marker: MarkerImageDefinition,
  glyphColor: string,
): ImageData {
  const pixelRatio = PHOSPHOR_RUNTIME_ICON_PIXEL_RATIO;
  const logicalSize = marker.selected
    ? MARKER_SELECTED_SIZE
    : MARKER_NORMAL_SIZE;
  const size = logicalSize * pixelRatio;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Failed to get 2d context for marker rasterization");
  }

  const radius =
    (marker.selected ? MARKER_SELECTED_RADIUS : MARKER_NORMAL_RADIUS) *
    pixelRatio;
  const strokeWidth =
    (marker.selected
      ? MARKER_SELECTED_STROKE_WIDTH
      : MARKER_NORMAL_STROKE_WIDTH) * pixelRatio;
  const center = size / 2;

  ctx.clearRect(0, 0, size, size);
  ctx.beginPath();
  ctx.arc(center, center, radius, 0, Math.PI * 2);
  ctx.fillStyle = marker.backgroundColor;
  ctx.fill();
  ctx.lineWidth = strokeWidth;
  ctx.strokeStyle = marker.borderColor;
  ctx.stroke();

  const glyphCanvas = document.createElement("canvas");
  glyphCanvas.width = size;
  glyphCanvas.height = size;
  const glyphCtx = glyphCanvas.getContext("2d");
  if (!glyphCtx) {
    throw new Error("Failed to get 2d context for marker glyph rasterization");
  }

  const drawSize =
    (marker.selected ? MARKER_SELECTED_GLYPH_SIZE : MARKER_NORMAL_GLYPH_SIZE) *
    pixelRatio;
  const scale = Math.min(
    drawSize / img.naturalWidth,
    drawSize / img.naturalHeight,
  );
  const width = Math.max(1, Math.round(img.naturalWidth * scale));
  const height = Math.max(1, Math.round(img.naturalHeight * scale));
  const x = Math.floor((size - width) / 2);
  const y = Math.floor((size - height) / 2);

  glyphCtx.drawImage(img, x, y, width, height);
  glyphCtx.globalCompositeOperation = "source-in";
  glyphCtx.fillStyle = glyphColor;
  glyphCtx.fillRect(0, 0, size, size);
  glyphCtx.globalCompositeOperation = "source-over";
  ctx.drawImage(glyphCanvas, 0, 0);

  return ctx.getImageData(0, 0, size, size);
}

function getPhosphorIconUrl(name: string): string {
  return `${PHOSPHOR_DUOTONE_BASE_URL}/${name}-duotone.svg`;
}
